import queryString from 'query-string';
import { prepareParams } from 'features/invest/utils/prepareParams';
import { CALL_API } from 'middleware/api';
import { UPDATE_SAVING_RULES } from './types';
export const setAccountStatus = (status) => ({
    type: 'POTS_SET_ACCOUNT_STATUS',
    status,
});
export const savePotsOnboardingState = (payload) => ({
    type: 'SAVE_POTS_ONBOARDING_STATE',
    ...payload,
});
export const clearPotsOnboardingState = () => ({
    type: 'CLEAR_POTS_ONBOARDING_STATE',
});
export const getPotsAccountWithRetry = (retryTimes) => ({
    retryTimes,
    type: 'GET_POTS_ACCOUNT_WITH_RETRY',
});
export const createPotsAccount = (pot, savingsPlan, initialDepositAmount, accountId) => ({
    [CALL_API]: {
        endpoint: '/wealth/pots/account',
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            pot,
            ...(accountId !== undefined && { accountId }),
            savingsPlan,
            ...(initialDepositAmount && {
                oneOffDeposit: {
                    amount: initialDepositAmount,
                },
            }),
        }),
        types: ['CREATE_POTS_ACCOUNT_REQUEST', 'CREATE_POTS_ACCOUNT_SUCCESS', 'CREATE_POTS_ACCOUNT_FAILURE'],
    },
});
export const getPotsAccount = () => ({
    [CALL_API]: {
        endpoint: '/wealth/pots/account',
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        types: ['GET_POTS_ACCOUNT_REQUEST', 'GET_POTS_ACCOUNT_SUCCESS', 'GET_POTS_ACCOUNT_FAILURE'],
    },
});
export const getSavingsPlan = () => ({
    [CALL_API]: {
        endpoint: '/wealth/pots/savings-plan',
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        types: ['GET_SAVINGS_PLAN_REQUEST', 'GET_SAVINGS_PLAN_SUCCESS', 'GET_SAVINGS_PLAN_FAILURE'],
    },
});
export const getPotsEstimatedCollectionDate = () => ({
    [CALL_API]: {
        endpoint: '/wealth/pots/direct-debit/estimated-collection-date',
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        types: [
            'GET_POTS_ESTIMATED_COLLECTION_DATE_REQUEST',
            'GET_POTS_ESTIMATED_COLLECTION_DATE_SUCCESS',
            'GET_POTS_ESTIMATED_COLLECTION_DATE_FAILURE',
        ],
    },
});
/**
 * `GET /wealth/pots`
 * @returns All user pots
 */
export function getPots() {
    return {
        [CALL_API]: {
            endpoint: '/wealth/pots?withDeleted=true',
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            types: ['GET_POTS_REQUEST', 'GET_POTS_SUCCESS', 'GET_POTS_ERROR'],
        },
    };
}
export function getSpacePots(spaceId) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/spaces/${spaceId}/pots`,
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            extra: { spaceId },
            types: ['GET_SPACE_POTS_REQUEST', 'GET_SPACE_POTS_SUCCESS', 'GET_SPACE_POTS_ERROR'],
        },
    };
}
export function setPotSpace(potId, spaceId) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/spaces/${spaceId}/add-pots`,
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                potIds: [potId],
            }),
            types: ['SET_POT_SPACE_REQUEST', 'SET_POT_SPACE_SUCCESS', 'SET_POT_SPACE_FAILURE'],
            extra: { spaceId },
        },
    };
}
/**
 * Create a new pot.
 * `POST /wealth/pots`
 * @returns The newly created pot
 */
export function createPot(name, emoji, type, productId, defaultSpaceId, goalAmount, goalDate) {
    return {
        [CALL_API]: {
            endpoint: '/wealth/pots',
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                pot: {
                    name,
                    emoji,
                    type,
                    productId,
                    ...(goalAmount && { goal: { amount: Number(goalAmount), date: goalDate } }),
                },
            }),
            types: ['CREATE_POT_REQUEST', 'CREATE_POT_SUCCESS', 'CREATE_POT_ERROR'],
            extra: { defaultSpaceId },
        },
    };
}
export function updatePot(pot) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/pots`,
            method: 'PUT',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ pot }),
            types: ['UPDATE_POT_REQUEST', 'UPDATE_POT_SUCCESS', 'UPDATE_POT_FAILURE'],
        },
    };
}
/**
 * Delete a pot.
 * `DELETE /wealth/pots/{potId}`
 * @param potId The ID of the pot to delete. You can even delete it multiple times - no error will be raised on backend.
 * @returns availableToInvest
 */
export function deletePot(potId) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/pots/${potId}`,
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
            },
            types: ['DELETE_POT_REQUEST', 'DELETE_POT_SUCCESS', 'DELETE_POT_ERROR'],
            extra: { potId },
        },
    };
}
export function reopenPot(potId) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/pots/${potId}/reopen`,
            method: 'POST',
            headers: {
                Accept: 'application/json',
            },
            types: ['REOPEN_POT_REQUEST', 'REOPEN_POT_SUCCESS', 'REOPEN_POT_ERROR'],
        },
    };
}
/**
 * Execute internal transfer between pots or to/from available balance.
 * `POST /wealth/pots/transfer`
 */
export function internalTransfer(fromPotId, toPotId, amount) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/pots/${fromPotId}/transfer`,
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ amount, toPotId }),
            types: ['INTERNAL_TRANSFER_REQUEST', 'INTERNAL_TRANSFER_SUCCESS', 'INTERNAL_TRANSFER_ERROR'],
            reuseXRequestIdOnRetry: true,
        },
    };
}
/**
 * Execute a one-off Direct Debit deposit into a pot
 * `POST /wealth/pots/${potId}/deposit-from-direct-debit`
 */
export function depositPotFromDirectDebit(potId, amount) {
    return {
        [CALL_API]: {
            endpoint: `/wealth/pots/${potId}/deposit-from-direct-debit`,
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ amount: amount.value }),
            types: ['DEPOSIT_POT_DIRECT_DEBIT_REQUEST', 'DEPOSIT_POT_DIRECT_DEBIT_SUCCESS', 'DEPOSIT_POT_DIRECT_DEBIT_ERROR'],
        },
    };
}
/**
 * Accept the Direct Debit mandate for autosave deposits into a pot.
 * `POST /wealth/pots/direct-debit-mandate`
 */
export function acceptAutosaveMandate(sortCode, accountNumber) {
    return {
        [CALL_API]: {
            endpoint: '/wealth/pots/direct-debit-mandate',
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sortCode, accountNumber }),
            types: ['ACCEPT_AUTOSAVE_MANDATE_REQUEST', 'ACCEPT_AUTOSAVE_MANDATE_SUCCESS', 'ACCEPT_AUTOSAVE_MANDATE_FAILURE'],
        },
    };
}
export const withdrawPotToConnectedAccount = (potId, amount, currency, pin) => ({
    [CALL_API]: {
        endpoint: `/wealth/pots/${potId}/withdraw-to-bank`,
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: { value: amount, currency } }),
        types: [
            'WITHDRAW_POT_TO_CONNECTED_ACCOUNT_REQUEST',
            'WITHDRAW_POT_TO_CONNECTED_ACCOUNT_SUCCESS',
            'WITHDRAW_POT_TO_CONNECTED_ACCOUNT_FAILURE',
        ],
        pin,
        muteAlert: true,
        scope: 'order:write',
        reuseXRequestIdOnRetry: true,
    },
});
export function getPotActivities(potId, params) {
    return {
        [CALL_API]: {
            muteAlert: true,
            endpoint: `/wealth/pots/${potId}/activity?${queryString.stringify(prepareParams({ ...params, includeAutoSave: true }))}`,
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            extra: { potId, params },
            types: ['GET_POT_ACTIVITIES_REQUEST', 'GET_POT_ACTIVITIES_SUCCESS', 'GET_POT_ACTIVITIES_FAILURE'],
        },
    };
}
export const getAllPotsActivities = (paging) => ({
    [CALL_API]: {
        muteAlert: true,
        endpoint: `/wealth/pots/activity?${queryString.stringify(prepareParams({ paging }))}`,
        method: 'GET',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        types: ['GET_ALL_POTS_ACTIVITIES_REQUEST', 'GET_ALL_POTS_ACTIVITIES_SUCCESS', 'GET_ALL_POTS_ACTIVITIES_FAILURE'],
    },
});
export const getPotsInformation = () => ({
    [CALL_API]: {
        endpoint: '/wealth/pots/products?withCashISA=true&types[]=BASIC&types[]=INTEREST&types[]=INTEREST_NOTICE',
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        types: ['GET_POTS_INFORMATION_REQUEST', 'GET_POTS_INFORMATION_SUCCESS', 'GET_POTS_INFORMATION_FAILURE'],
    },
});
export function createSavingsPlan(bankAccount, allowOverdraft, distribution, rules, collectionFrequency) {
    const destinations = [];
    Object.keys(distribution).forEach((id) => {
        if (distribution[id]) {
            destinations.push({
                potId: id,
                percentage: distribution[id],
            });
        }
    });
    return {
        [CALL_API]: {
            endpoint: '/wealth/pots/savings-plan',
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                rules,
                destinations,
                allowOverdraft,
                collectionFrequency,
                accountId: bankAccount.id,
            }),
            types: ['CREATE_SAVINGS_PLAN_REQUEST', 'CREATE_SAVINGS_PLAN_SUCCESS', 'CREATE_SAVINGS_PLAN_ERROR'],
        },
    };
}
const getPatchData = (data) => {
    if (data.distribution) {
        const { distribution, ...otherData } = data;
        const destinations = [];
        Object.keys(distribution).forEach((id) => {
            if (distribution[id]) {
                destinations.push({
                    potId: id,
                    percentage: distribution[id],
                });
            }
        });
        return {
            destinations,
            ...otherData,
        };
    }
    return data;
};
export const editSavingsPlan = (id, data, skipSet) => {
    const patchData = getPatchData(data);
    return {
        [CALL_API]: {
            endpoint: `/wealth/pots/savings-plan/${id}`,
            method: 'PATCH',
            body: JSON.stringify(patchData),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            types: ['EDIT_SAVINGS_PLAN_REQUEST', 'EDIT_SAVINGS_PLAN_SUCCESS', 'EDIT_SAVINGS_PLAN_FAILURE'],
            extra: { savingsPlanId: id, skipSet },
        },
    };
};
export const getAutosaveActivities = (paging) => ({
    [CALL_API]: {
        endpoint: `/wealth/pots/savings-plan/plan-activity?${queryString.stringify(prepareParams(paging))}`,
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        extra: paging,
        types: ['GET_AUTOSAVE_ACTIVITIES_REQUEST', 'GET_AUTOSAVE_ACTIVITIES_SUCCESS', 'GET_AUTOSAVE_ACTIVITIES_FAILURE'],
    },
});
export const editAutosaveUpcomingPayment = (id, skip) => ({
    [CALL_API]: {
        endpoint: `/wealth/pots/savings-plan/upcoming-payment/${id}`,
        method: 'PATCH',
        body: JSON.stringify({ skip }),
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        types: [
            'EDIT_AUTOSAVE_UPCOMING_PAYMENT_REQUEST',
            'EDIT_AUTOSAVE_UPCOMING_PAYMENT_SUCCESS',
            'EDIT_AUTOSAVE_UPCOMING_PAYMENT_FAILURE',
        ],
    },
});
export const updateSavingRules = (payload) => ({
    payload,
    type: UPDATE_SAVING_RULES,
});
export const updateSavingRulesFailure = () => ({
    payload: 'EventBus.Pots.UPDATE_SAVING_RULES_FAILURE',
    type: 'EVENT_BUS.EVENT',
});
export const depositedToPot = () => ({
    type: 'DEPOSITED_TO_POT',
});
export const topUpPotUsingObCreated = () => ({
    type: 'TOP_UP_POT_USING_OB_CREATED',
});
